/**
 * View More Button Component
 *
 * A reusable button for pagination that allows users to view more results.
 * It shows both a "View More" button and a results count summary.
 *
 * Features:
 * - Shows count of displayed results versus total
 * - Conditionally renders the View More button based on data availability
 * - Handles navigation to the full results page
 * - Internationalization support
 *
 * @module components/view-more-button
 */

import { Button } from "react-bootstrap";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

/**
 * View More Button component with results counter
 *
 * @param {Object} props - Component properties
 * @param {string} props.id - ID (usually customer ID) to add to query params
 * @param {string} props.path - Target path to navigate to for full results
 * @param {number} props.totalDocs - Total number of available results
 * @param {URLSearchParams} props.searchParams - Current URL search parameters
 * @param {number} props.resultsLength - Number of currently displayed results
 * @param {Function} props.handleQueryParams - Function to update query parameters
 * @returns {JSX.Element} View more button and results counter
 */
const ViewMoreButton = ({
  id,
  path,
  totalDocs,
  searchParams,
  resultsLength,
  handleQueryParams
}) => {
  const { t } = useTranslation();

  // Only show View More button if there are more results available
  const showViewMore = totalDocs > resultsLength;
  const navigate = useNavigate();

  /**
   * Handle view more button click
   * Adds customer ID to query params and navigates to the target path
   */
  const handleViewMore = () => {
    handleQueryParams("customer", id);
    navigate({
      pathname: path,
      search: searchParams.toString()
    });
  };
  /**
   * Render the button and results counter
   */
  return (
    <div className="mb-3 d-flex align-items-center">
      {/* View More button - only shown if more results are available */}
      {showViewMore && (
        <div className="flex-fill text-center">
          <Button variant="outline-secondary me-2" onClick={handleViewMore}>
            {t("viewMore")}
          </Button>
        </div>
      )}

      {/* Results counter - always shown */}
      <div
        className={`text-muted ${!showViewMore ? "flex-fill text-end" : ""}`}
      >
        {resultsLength} {t("common.of")} {totalDocs} {t("common.results")}
      </div>
    </div>
  );
};

export default ViewMoreButton;
